import { FormikProps } from 'formik';
import React, { RefObject, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { noop } from 'lodash';
import useReduxModal from './reduxHelperHooks/useReduxModal';

// this hook displays a discard warning modal when a user attempts to navigate away
// from a form after touching or modifying its values
const useIsLeavingForm = (formRef?: RefObject<FormikProps<any>>) => {
    const { addTechnicalErrorModal, clearAll } = useReduxModal();
    const { block, push } = useHistory();
    const { t } = useTranslation();
    // react-router-dom provides a way to "block" history changes
    // use a ref to keep track of the blocker and unblocker function.
    const historyBlockRef = React.useRef<() => void>(noop);

    useEffect(() => {
        historyBlockRef.current = block((location, action) => {
            const isFormChanged = formRef?.current?.dirty;
            const isFormSubmitting = formRef?.current?.isSubmitting;
            const nextLocation = location.pathname;
            // form has been touched, but not submitted
            if (isFormChanged && !isFormSubmitting) {
                addTechnicalErrorModal('', t('DiscardChangesWarningModalTitle'), clearAll, {
                    status: 'warning',
                    content: t('DiscardChangesWarningModalSubTitle'),
                    onActionText: t('Confirm'),
                    onAction: () => {
                        historyBlockRef.current(); // unblock the navigation
                        clearAll();
                        push(nextLocation);
                    },
                    cancelText: t('Cancel')
                });
            }

            // form is not changed and user wants to navigate away
            if (!isFormChanged && action === 'PUSH') {
                historyBlockRef.current(); // unblock the navigation
                push(nextLocation);
            }

            if (isFormSubmitting) {
                historyBlockRef.current(); // unblock the navigation
                push(nextLocation);
            }

            return false;
        });

        return () => {
            // stop listening
            historyBlockRef.current();
        };
    }, [formRef]);
};

export default useIsLeavingForm;
